import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ThreeDotsVertical } from "react-bootstrap-icons";
import { useParams } from "react-router-dom";
import {
  type CaseFileLite,
  DocumentStatus,
  type PurgatorySummary,
} from "@/briefpoint-client";
import Button from "@/components/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/components/ui/toast";
import { useCaseFileApi } from "@/hooks/useApi";
import PdfIcon from "@/images/new-pdf-icon.svg?react";
import {
  getCaseFilesWithheldListOptions,
  getPurgatorySummaryOptions,
  searchCaseFilesQueryOptions,
} from "@/lib/query-options";
import styles from "./WithheldFileItem.module.scss";

interface Props {
  file: CaseFileLite;
  onSelect: (fileId: string) => void;
}

const WithheldFileItem = (props: Props) => {
  const urlParams = useParams<{
    id: string;
    firstFolderId: string;
    secondFolderId: string;
    thirdFolderId: string;
  }>();
  const caseFileApi = useCaseFileApi();
  const queryClient = useQueryClient();
  const isProcessed = props.file.status === DocumentStatus.Processed;

  // Production documents request, used by the WithheldFileLists component
  const getCaseFilesWithheldListQueryOptions = getCaseFilesWithheldListOptions({
    caseFileApi,
    caseId: urlParams.id,
  });

  // Count of all not-withheld/not-privileged uploaded files, used by the UploadMore component
  const purgatorySummaryQueryOptions = getPurgatorySummaryOptions(
    caseFileApi,
    urlParams.id,
  );

  // MUTATIONS
  // Move to Produce - Changes Privileged and Withheld flags to false
  const setMoveProduceMutation = useMutation({
    mutationFn: async () => {
      if (!urlParams.id) throw new Error("Case ID is required");
      return Promise.all([
        caseFileApi.caseFileSetPrivileged({
          fileId: props.file.id,
          caseId: urlParams.id,
          body: false,
        }),
        caseFileApi.caseFileSetWithheld({
          fileId: props.file.id,
          caseId: urlParams.id,
          body: false,
        }),
      ]);
    },
    onMutate: async () => {
      // Optimistically update the data
      await Promise.all([
        queryClient.cancelQueries({
          queryKey: getCaseFilesWithheldListQueryOptions.queryKey,
        }),
        // If the file is 'Processed', it moves directly to Produce so we don't need to update the UploadMore count
        ...(isProcessed
          ? []
          : [
              queryClient.cancelQueries({
                queryKey: purgatorySummaryQueryOptions.queryKey,
              }),
            ]),
      ]);

      // Snapshot the previous value of the query data
      const previousCaseFilesQueryData = queryClient.getQueryData(
        getCaseFilesWithheldListQueryOptions.queryKey,
      );
      const previousPurgatorySummaryQueryData = queryClient.getQueryData(
        purgatorySummaryQueryOptions.queryKey,
      );

      // Optimistically update case files query data
      queryClient.setQueryData(
        getCaseFilesWithheldListQueryOptions.queryKey,
        (old: CaseFileLite[] | undefined) => {
          if (!old) return old;
          return old.map((file) =>
            file.id === props.file.id
              ? { ...file, isPrivileged: false, isWithheld: false }
              : file,
          );
        },
      );

      // Optimistically update purgatory summary query data if the file is not 'Processed'
      if (!isProcessed) {
        queryClient.setQueryData(
          purgatorySummaryQueryOptions.queryKey,
          (oldData: PurgatorySummary | undefined) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              fileCount: oldData.fileCount + 1,
            };
          },
        );
      }

      return { previousCaseFilesQueryData, previousPurgatorySummaryQueryData };
    },
    onSuccess: () => {
      if (isProcessed) {
        toast.success(`${props.file.name} was moved to Produce`);
      } else {
        toast.success(`${props.file.name} is ready for review`);
      }
    },
    onError: (_, __, context) => {
      if (context?.previousCaseFilesQueryData) {
        queryClient.setQueryData(
          getCaseFilesWithheldListQueryOptions.queryKey,
          context.previousCaseFilesQueryData,
        );
      }
      if (context?.previousPurgatorySummaryQueryData) {
        queryClient.setQueryData(
          purgatorySummaryQueryOptions.queryKey,
          context.previousPurgatorySummaryQueryData,
        );
      }

      toast.error(`Error moving ${props.file.name} to Produce`);
    },

    onSettled: () => {
      // Invalidate the case files query to refresh the data
      queryClient.invalidateQueries({
        queryKey: getCaseFilesWithheldListQueryOptions.queryKey,
      });
      // If the file moved to produce is a 'Processed' file, it moves directly to Produce so we need to refetch the file explorer data on the Produce tab
      if (isProcessed) {
        queryClient.invalidateQueries({
          queryKey: searchCaseFilesQueryOptions(caseFileApi, {
            caseId: urlParams.id,
          }).queryKey,
        });
      } else {
        // Otherwise, it moves to the PurgatoryModal so we need to refetch the document count
        queryClient.invalidateQueries({
          queryKey: purgatorySummaryQueryOptions.queryKey,
        });
      }
    },
  });

  // Change Privileged Flag
  const setPrivilegedMutation = useMutation({
    mutationFn: async (isPrivileged: boolean) => {
      if (!urlParams.id) throw new Error("Case ID is required");
      return caseFileApi.caseFileSetPrivileged({
        fileId: props.file.id,
        caseId: urlParams.id,
        body: isPrivileged,
      });
    },
    onMutate: async (isPrivileged) => {
      // Optimistically update the data
      await queryClient.cancelQueries({
        queryKey: getCaseFilesWithheldListQueryOptions.queryKey,
      });

      const previousData = queryClient.getQueryData(
        getCaseFilesWithheldListQueryOptions.queryKey,
      );

      queryClient.setQueryData(
        getCaseFilesWithheldListQueryOptions.queryKey,
        (old: CaseFileLite[] | undefined) => {
          if (!old) return old;
          return old.map((file) =>
            file.id === props.file.id ? { ...file, isPrivileged } : file,
          );
        },
      );

      return { previousData };
    },
    onSuccess: (_, isPrivileged) => {
      toast.success(
        isPrivileged
          ? `${props.file.name} was moved to Privileged Withheld`
          : `${props.file.name} was moved to Other Withheld`,
        {
          action: {
            label: "Undo",
            onClick: () => {
              setPrivilegedMutation.mutate(!isPrivileged);
            },
          },
        },
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: getCaseFilesWithheldListQueryOptions.queryKey,
      });
    },
    onError: (_, isPrivileged, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          getCaseFilesWithheldListQueryOptions.queryKey,
          context.previousData,
        );
      }
      toast.error(
        isPrivileged
          ? `Error designating ${props.file.name} as Privileged Withheld`
          : `Error designating ${props.file.name} as Other Withheld`,
      );
    },
  });

  // Deletes the Document
  const deleteFileMutation = useMutation({
    mutationFn: async () => {
      if (!urlParams.id) throw new Error("Case ID is required");
      return caseFileApi.caseFileDelete({
        fileId: props.file.id,
        caseId: urlParams.id,
      });
    },
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: getCaseFilesWithheldListQueryOptions.queryKey,
      });

      const previousData = queryClient.getQueryData(
        getCaseFilesWithheldListQueryOptions.queryKey,
      );

      queryClient.setQueryData(
        getCaseFilesWithheldListQueryOptions.queryKey,
        (old: CaseFileLite[] | undefined) => {
          if (!old) return old;
          return old.filter((file) => file.id !== props.file.id);
        },
      );

      return { previousData };
    },
    onSuccess: () => {
      toast.success(`${props.file.name} was deleted`);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: getCaseFilesWithheldListQueryOptions.queryKey,
      });
    },
    onError: (_, __, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          getCaseFilesWithheldListQueryOptions.queryKey,
          context.previousData,
        );
      }
      toast.error("Error deleting file");
    },
  });

  return (
    <div
      data-testid="withheld-file-item"
      className={`p-2 ${styles.withheldFileItem}`}
    >
      <Button
        variant="link"
        className={
          "d-flex justify-content-between w-100 border-0 p-0 text-decoration-none"
        }
        onClick={() => {
          props.onSelect(props.file.id);
        }}
      >
        <div>
          <PdfIcon className="me-2" />
          <span>{props.file.name}</span>
        </div>
        <div
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild className={styles.trigger}>
              <ThreeDotsVertical size={14} color="var(--text)" />
            </DropdownMenuTrigger>
            <DropdownMenuPortal>
              <DropdownMenuContent>
                {props.file.isPrivileged && (
                  <DropdownMenuItem
                    data-testid="move-to-other-withheld"
                    className={styles.dropDownOption}
                    onClick={() => {
                      setPrivilegedMutation.mutate(false);
                    }}
                  >
                    Move to Other Withheld
                  </DropdownMenuItem>
                )}
                {props.file.isWithheld && !props.file.isPrivileged && (
                  <DropdownMenuItem
                    data-testid="move-to-privileged-withheld"
                    className={styles.dropDownOption}
                    onClick={() => {
                      setPrivilegedMutation.mutate(true);
                    }}
                  >
                    Move to Privileged Withheld
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  data-testid="move-to-produce"
                  className={styles.dropDownOption}
                  onClick={() => {
                    setMoveProduceMutation.mutate();
                  }}
                >
                  Move to Produce
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="delete-file"
                  className={`${styles.dropDownOption} text-danger`}
                  onClick={() => {
                    deleteFileMutation.mutate();
                  }}
                >
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenuPortal>
          </DropdownMenu>
        </div>
      </Button>
    </div>
  );
};

export default WithheldFileItem;
